import { useCallback, useEffect, useRef, useState } from "react";
import type { CheckinFrame } from "@/types";
import { CHECKIN_PHASE_LABELS, checkinClock } from "@/lib/walkaround";
import { captureThumbnail, pickRecorderFormat, uploadCheckinVideo } from "@/lib/checkinMedia";

type RecorderState = "requesting" | "ready" | "recording" | "review" | "blocked";

export interface RecorderFinishPayload {
  recordingId: number | null;
  phase: string;
  frames: CheckinFrame[];
  videoPath: string;
  videoMime: string;
  videoSize: number;
  durationSeconds: number;
  note: string;
}

interface CheckinRecorderProps {
  bookingId: number;
  defaultPhase: "pickup" | "return";
  saving: boolean;
  onStart: (phase: string) => Promise<number | null>;
  onFinish: (payload: RecorderFinishPayload) => Promise<void>;
  onCancel: (recordingId: number | null) => void;
}

const MAX_THUMBS = 8;
const THUMB_INTERVAL_MS = 2500;

export default function CheckinRecorder({
  bookingId,
  defaultPhase,
  saving,
  onStart,
  onFinish,
  onCancel,
}: CheckinRecorderProps) {
  const [state, setState] = useState<RecorderState>("requesting");
  const [phase, setPhase] = useState<"pickup" | "return">(defaultPhase);
  const [seconds, setSeconds] = useState(0);
  const [thumbs, setThumbs] = useState<CheckinFrame[]>([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [recordedUrl, setRecordedUrl] = useState("");
  const [camNote, setCamNote] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const mimeRef = useRef<string>("video/webm");
  const secondsRef = useRef(0);
  const recordingIdRef = useRef<number | null>(null);
  const thumbTimerRef = useRef<number | null>(null);
  const secondTimerRef = useRef<number | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const clearTimers = useCallback(() => {
    if (thumbTimerRef.current) window.clearInterval(thumbTimerRef.current);
    if (secondTimerRef.current) window.clearInterval(secondTimerRef.current);
    thumbTimerRef.current = null;
    secondTimerRef.current = null;
  }, []);

  const requestCamera = useCallback(async () => {
    setCamNote("");
    setError("");
    setState("requesting");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamNote("This browser doesn't expose a camera to the page.");
      setState("blocked");
      return;
    }
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: true,
        });
      } catch {
        // Some laptops have no mic — fall back to a video-only stream.
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
      }
      setState("ready");
    } catch {
      setCamNote("We couldn't reach your camera. Check the browser permission (the camera icon in the address bar) and try again.");
      setState("blocked");
    }
  }, []);

  useEffect(() => {
    requestCamera();
    return () => {
      clearTimers();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.stop();
        } catch {
          // ignore
        }
      }
      stopStream();
    };
  }, [requestCamera, clearTimers, stopStream]);

  const captureThumb = useCallback(() => {
    const dataUrl = captureThumbnail(videoRef.current);
    if (!dataUrl) return;
    setThumbs((prev) => {
      if (prev.length >= MAX_THUMBS) return prev;
      return [...prev, { url: dataUrl, label: `Still ${prev.length + 1}`, at: secondsRef.current }];
    });
  }, []);

  const beginRecording = useCallback(async () => {
    if (!streamRef.current) return;
    setError("");
    setSeconds(0);
    secondsRef.current = 0;
    setThumbs([]);
    chunksRef.current = [];
    blobRef.current = null;
    setRecordedUrl("");

    if (recordingIdRef.current === null) {
      recordingIdRef.current = await onStart(phase);
    }

    const format = pickRecorderFormat();
    mimeRef.current = format.mimeType || "video/webm";
    try {
      const recorder = new MediaRecorder(
        streamRef.current,
        format.mimeType ? { mimeType: format.mimeType } : undefined
      );
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        blobRef.current = blob;
        setRecordedUrl(URL.createObjectURL(blob));
      };
      recorderRef.current = recorder;
      recorder.start(1000);
    } catch {
      setError("Recording isn't supported in this browser. Try a newer Chrome, Edge or Safari.");
      return;
    }

    setState("recording");
    secondTimerRef.current = window.setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
    }, 1000);
    thumbTimerRef.current = window.setInterval(captureThumb, THUMB_INTERVAL_MS);
    window.setTimeout(captureThumb, 600);
  }, [onStart, phase, captureThumb]);

  const stopRecording = useCallback(() => {
    clearTimers();
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch {
      // ignore
    }
    setState("review");
  }, [clearTimers]);

  const discardRecording = useCallback(() => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl("");
    blobRef.current = null;
    chunksRef.current = [];
    setThumbs([]);
    setSeconds(0);
    secondsRef.current = 0;
    setState("ready");
  }, [recordedUrl]);

  const handleClose = useCallback(() => {
    clearTimers();
    stopStream();
    onCancel(recordingIdRef.current);
  }, [clearTimers, stopStream, onCancel]);

  const handleSave = useCallback(async () => {
    const blob = blobRef.current;
    if (!blob || blob.size === 0) {
      setError("There's nothing recorded yet. Start the camera and film the car first.");
      return;
    }
    setError("");
    try {
      const { path, size } = await uploadCheckinVideo(bookingId, phase, blob, blob.type);
      await onFinish({
        recordingId: recordingIdRef.current,
        phase,
        frames: thumbs,
        videoPath: path,
        videoMime: blob.type || mimeRef.current,
        videoSize: size,
        durationSeconds: secondsRef.current,
        note,
      });
      clearTimers();
      stopStream();
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't save this walkaround. Please try again.");
    }
  }, [bookingId, phase, thumbs, note, onFinish, clearTimers, stopStream]);

  const recording = state === "recording";
  const blocked = state === "blocked";
  const progress = recording ? Math.min(100, (seconds / 45) * 100) : state === "review" ? 100 : 0;

  return (
    <div className="fixed inset-0 z-[1200] flex flex-col bg-background-50/98 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200/70 px-4 py-3.5 md:px-6">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full ${
              recording ? "bg-secondary-500/20 text-secondary-400" : "bg-primary-500/20 text-primary-400"
            }`}
          >
            <i className={`${recording ? "ri-record-circle-line animate-rec-pulse" : "ri-camera-lens-line"} text-lg`}></i>
          </span>
          <div>
            <p className="font-heading text-sm font-semibold text-foreground-950">
              {recording ? "Recording walkaround" : state === "review" ? "Review your walkaround" : "Film a walkaround"}
            </p>
            <p className="flex items-center gap-2 text-xs text-foreground-600">
              {recording ? (
                <>
                  <span className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-secondary-400 animate-rec-pulse"></span> REC {checkinClock(seconds)}
                  </span>
                  <span className="text-foreground-500">·</span>
                  <span>{thumbs.length} stills captured</span>
                </>
              ) : state === "requesting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Asking for camera access…
                </>
              ) : blocked ? (
                "Camera blocked"
              ) : state === "review" ? (
                `${checkinClock(seconds)} recorded`
              ) : (
                "Camera ready — film all sides of the car"
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(["pickup", "return"] as const).map((option) => (
            <button
              key={option}
              type="button"
              disabled={recording}
              onClick={() => setPhase(option)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                phase === option
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-500/50"
              }`}
            >
              {CHECKIN_PHASE_LABELS[option]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">
        <div className="mx-auto w-full max-w-3xl">
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-background-200/70 bg-background-200/60">
            {state === "review" && recordedUrl ? (
              <video src={recordedUrl} controls playsInline className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`absolute inset-0 h-full w-full object-cover ${state === "requesting" || blocked ? "opacity-0" : "opacity-100"}`}
              />
            )}

            {recording && (
              <>
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24 animate-scan bg-gradient-to-b from-primary-500/15 to-transparent" />
                <div className="absolute left-3 top-3 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-500/90 px-3 py-1 text-[11px] font-medium text-background-50">
                    <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-rec-pulse"></span> REC
                  </span>
                  <span className="rounded-full bg-background-50/85 px-3 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur">
                    {CHECKIN_PHASE_LABELS[phase]}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-50/40">
                    <div className="h-full rounded-full bg-primary-500 transition-all duration-700" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </>
            )}

            {(state === "requesting" || blocked) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
                  <i className={state === "requesting" ? "ri-loader-4-line animate-spin" : "ri-camera-off-line"}></i>
                </span>
                <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {state === "requesting" ? "Opening your camera…" : "Camera access needed"}
                </p>
                {blocked && (
                  <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-600">{camNote}</p>
                )}
                {blocked && (
                  <button
                    type="button"
                    onClick={requestCamera}
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line"></i> Try camera again
                  </button>
                )}
              </div>
            )}
          </div>

          {blocked && (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-background-200/70 bg-background-100 px-3.5 py-2.5 text-xs leading-relaxed text-foreground-600">
              <i className="ri-information-line mt-0.5 text-primary-500"></i>
              A real camera is required for check-in walks — the clip is stored on the booking so it can be compared after the ride.
            </p>
          )}

          {thumbs.length > 0 && (
            <div className="mt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-foreground-500">Captured stills</p>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                {thumbs.map((frame, index) => (
                  <button
                    key={`${frame.label}-${index}`}
                    type="button"
                    onClick={() => {
                      const el = videoRef.current;
                      if (el && state !== "review") el.currentTime = frame.at;
                    }}
                    className="h-14 w-20 shrink-0 overflow-hidden rounded-lg border border-background-200/70"
                    title={`${frame.label} · ${checkinClock(frame.at)}`}
                  >
                    <img src={frame.url} alt={frame.label} title={frame.label} className="h-full w-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="mt-4 block">
            <span className="text-xs font-medium uppercase tracking-wider text-foreground-500">Condition notes (optional)</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, 300))}
              rows={3}
              maxLength={300}
              placeholder="e.g. Small scratch on the rear bumper, fuel at half a tank, both mats present…"
              className="mt-2 w-full resize-none rounded-xl border border-background-200/70 bg-background-100 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
            />
          </label>

          {error && (
            <p className="mt-3 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-2.5 text-sm text-secondary-300">
              <i className="ri-error-warning-line"></i> {error}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-background-200/70 px-4 py-4 md:px-6">
        <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center justify-end gap-3">
          {state === "review" && (
            <button
              type="button"
              onClick={discardRecording}
              disabled={saving}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/50 disabled:opacity-50"
            >
              <i className="ri-restart-line"></i> Re-record
            </button>
          )}

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/50 disabled:opacity-50"
          >
            <i className="ri-close-line"></i> {state === "review" ? "Discard & close" : "Cancel"}
          </button>

          {recording ? (
            <button
              type="button"
              onClick={stopRecording}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              <i className="ri-stop-circle-line"></i> Stop recording
            </button>
          ) : state === "review" ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-cloud-2-line"></i>}
              {saving ? "Uploading…" : "Save walkaround"}
            </button>
          ) : (
            <button
              type="button"
              onClick={beginRecording}
              disabled={state !== "ready"}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              <i className="ri-record-circle-line"></i> Start recording
            </button>
          )}
        </div>
      </div>
    </div>
  );
}